import React, { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";

/**
 * CityAutocomplete — type-ahead city picker backed by OpenStreetMap Nominatim
 * (free, no API key). Returns the display string "City, State, Country".
 */
export default function CityAutocomplete({ value, onChange, placeholder = "e.g. Mumbai, Delhi, Kolkata", required = false, name = "city" }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Keep external value in sync (e.g. when reset)
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => fetchSuggestions(v.trim()), 300);
  };

  const fetchSuggestions = async (q) => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await res.json();
      // Build a clean "City, State, Country" display for every result that has
      // an address payload — relying on result class/type is too strict for
      // short prefixes (Nominatim sometimes returns amenities/shops for 'Mum').
      const items = data
        .map(d => {
          const a = d.address || {};
          const city = a.city || a.town || a.village || a.municipality || a.locality || a.county || a.suburb || "";
          const state = a.state || a.region || "";
          const country = a.country || "";
          const display = [city, state, country].filter(Boolean).join(", ");
          // Fall back to the raw display_name if address parts are missing
          return { display: display || (d.display_name || "").split(",").slice(0, 3).join(",").trim(), lat: d.lat, lon: d.lon };
        })
        .filter(it => it.display)
        .filter((it, idx, arr) => arr.findIndex(x => x.display === it.display) === idx)  // dedupe
        .slice(0, 6);
      setSuggestions(items);
      setOpen(items.length > 0);
      setHighlight(-1);
    } catch {
      setSuggestions([]); setOpen(false);
    }
  };

  const pick = (item) => {
    setQuery(item.display);
    onChange(item.display);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.35)", pointerEvents: "none" }}>
        <MapPin size={14} />
      </div>
      <input
        data-testid="city-autocomplete-input"
        type="text"
        name={name}
        value={query}
        onChange={handleInput}
        onKeyDown={handleKey}
        onFocus={() => suggestions.length && setOpen(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="new-password"
        spellCheck={false}
        className="cosmic-input"
        style={{ paddingLeft: "32px" }}
      />
      {open && suggestions.length > 0 && (
        <div data-testid="city-autocomplete-list" style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px",
          background: "rgba(28,10,46,0.98)", border: "1px solid rgba(255,209,102,0.2)",
          borderRadius: "10px", maxHeight: "240px", overflowY: "auto", zIndex: 30,
          backdropFilter: "blur(12px)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.5)"
        }}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              data-testid={`city-suggestion-${i}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "10px 14px", background: highlight === i ? "rgba(255,209,102,0.12)" : "transparent",
                border: 0, color: "rgba(255,255,255,0.88)", cursor: "pointer",
                fontSize: "0.86rem", borderBottom: i < suggestions.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                fontFamily: "'DM Sans', sans-serif"
              }}>
              {s.display}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
